// Découpage d'un remplacement prolongé par congé (plan 097 §16, règles D1 à D7
// et arbitrages du 19/10/2026). Fonction pure : aucune lecture, aucune
// écriture ; les données viennent de fetchSplitContext.
//
// Vocabulaire :
// - vacation : une date de R dont le jour de semaine est coché ;
// - couverture d'un congé : union des `Absence` de ce congé sur le compte de R
//   (D1) ; une vacation est couverte si sa date tombe dans une de ces absences,
//   sans exiger que son jour de semaine y soit coché ;
// - morceau : suite de vacations couvertes par un même congé ; le premier
//   morceau garde l'id de R ;
// - trou : suite de vacations qui ne relèvent d'aucun congé (D4) ; un trou
//   « interne » sépare deux suites de vacations d'un même congé (couverture
//   du congé en deux absences séparées sur le site).

import {
  addDays,
  formatDay,
  isWithin,
  maxDate,
  minDate,
  normalizeDate,
  weekdayOf,
} from './dates';

export const STATUS = {
  COVERED: 'covered',
  SPLIT: 'split',
  RELINK: 'relink',
  NO_NEXT: 'no_next',
  ORPHANS: 'orphans',
  PENDING: 'pending',
  UNCOVERED: 'uncovered',
  HOLE: 'hole',
};

export const CHOICE = {
  ATTACH: 'attach',
  REMOVE: 'remove',
};

const sameLeave = (left, right) => {
  return Number(left) === Number(right);
};

export const listVacations = ({
  start_date: start,
  end_date: end,
  weekdays,
}) => {
  const vacations = [];
  if (!start || !end || !weekdays || !weekdays.length) {
    return vacations;
  }
  for (let day = start; day <= end; day = addDays(day, 1)) {
    if (weekdays.includes(weekdayOf(day))) {
      vacations.push(day);
    }
  }
  return vacations;
};

const normalizeIntervals = (rows) => {
  return (rows || []).map((row) => {
    return {
      ...row,
      start_date: normalizeDate(row.start_date),
      end_date: normalizeDate(row.end_date),
    };
  });
};

// Couverture d'un congé : intervalles de ses absences, et bornes extrêmes
// pour l'affichage (« 12/10 → 29/10 », ou « à partir du 26/10 » si ouverte).
const buildCoverages = (absences) => {
  const coverages = {};
  absences.forEach((absence) => {
    const key = Number(absence.leave_id);
    if (!coverages[key]) {
      coverages[key] = {
        leaveId: key,
        intervals: [],
        start: absence.start_date,
        end: absence.end_date,
        open: false,
      };
    }
    const coverage = coverages[key];
    coverage.intervals.push({
      start: absence.start_date,
      end: absence.end_date,
    });
    coverage.start = minDate(coverage.start, absence.start_date);
    if (!absence.end_date) {
      coverage.open = true;
    }
    coverage.end = coverage.open
      ? null
      : maxDate(coverage.end, absence.end_date);
  });
  return coverages;
};

const covers = (coverage, date) => {
  return coverage.intervals.some((interval) => {
    return isWithin(date, interval.start, interval.end);
  });
};

const intervalStartFor = (coverage, date) => {
  const found = coverage.intervals
    .filter((interval) => {
      return isWithin(date, interval.start, interval.end);
    })
    .map((interval) => {
      return interval.start;
    });
  return minDate(...found);
};

const formatDates = (dates) => {
  const labels = dates.map((date) => {
    return `le ${formatDay(date)}`;
  });
  if (labels.length <= 1) {
    return labels.join('');
  }
  return `${labels.slice(0, -1).join(', ')} et ${labels[labels.length - 1]}`;
};

const formatCoverage = (coverage) => {
  if (coverage.open || !coverage.end) {
    return `à partir du ${formatDay(coverage.start)}`;
  }
  return `${formatDay(coverage.start)} → ${formatDay(coverage.end)}`;
};

const formatLeaves = (pieces, coverages) => {
  const labels = pieces.map((piece) => {
    return `${piece.leaveId} (${formatCoverage(coverages[piece.leaveId])})`;
  });
  if (labels.length === 1) {
    return `Le congé ${labels[0]} couvre`;
  }
  return `Les congés ${labels.slice(0, -1).join(', ')} et ${
    labels[labels.length - 1]
  } couvrent`;
};

// Congés de A sans aucune absence sur le compte, alors qu'ils visent ce site
// (sans `contract_id`, ou celui de R) et que A y a un poste ce jour-là (D5).
const findPendingLeaves = ({
  dates,
  leaves,
  coverages,
  postes,
  contractId,
}) => {
  const pending = {};
  dates.forEach((date) => {
    leaves.forEach((leave) => {
      const leaveId = Number(leave.id);
      if (coverages[leaveId]) {
        return;
      }
      if (leave.contract_id && leave.contract_id !== contractId) {
        return;
      }
      if (!isWithin(date, leave.start_date, leave.end_date)) {
        return;
      }
      const hasPoste = postes.some((poste) => {
        return isWithin(date, poste.start_date, poste.end_date);
      });
      if (hasPoste) {
        pending[leaveId] = true;
      }
    });
  });
  return Object.keys(pending)
    .map(Number)
    .sort((left, right) => {
      return left - right;
    });
};

/**
 * Bornes définitives des morceaux, d'après les choix de la personne sur
 * chaque trou (D4). Renvoie `null` tant qu'un trou n'a pas de choix.
 *
 * - un trou interne gardé laisse le morceau entier ; retiré, il coupe le
 *   morceau en deux remplacements du même congé ;
 * - début : le premier morceau garde le début de R (ou le jour qui suit un
 *   trou de tête retiré) ; un morceau suivant commence au début de sa
 *   couverture.
 * - `original` : le morceau qui garde l'id de R (celui du congé lié, sinon le
 *   premier).
 * - fin : min(fin du congé, veille du morceau suivant, fin de R) ; un trou
 *   rattaché étend le morceau qui précède, un trou retiré l'arrête la veille.
 */
export const resolvePieces = (plan, replacement, choices, options = {}) => {
  const { pieces, gaps } = plan;
  if (!pieces.length) {
    return [];
  }
  const replacementStart = normalizeDate(replacement.start_date);
  const replacementEnd = normalizeDate(replacement.end_date);
  const choiceFor = (gap) => {
    if (options.ignoreGaps) {
      return CHOICE.ATTACH;
    }
    return choices[gap.key];
  };
  const missing = gaps.some((gap) => {
    return !choiceFor(gap);
  });
  if (missing) {
    return null;
  }
  const outerGapAfter = (pieceIndex) => {
    return gaps.find((gap) => {
      return gap.position !== 'inside' && gap.afterPiece === pieceIndex;
    });
  };

  // Segments : un morceau, ou ses parts quand un trou interne est retiré.
  const segments = [];
  pieces.forEach((piece, pieceIndex) => {
    const runs = piece.runs || [
      {
        firstVacation: piece.firstVacation,
        lastVacation: piece.lastVacation,
        coverageStart: piece.coverageStart,
      },
    ];
    let current = null;
    runs.forEach((run, runIndex) => {
      const inner =
        runIndex > 0
          ? gaps.find((gap) => {
              return (
                gap.position === 'inside' &&
                gap.afterPiece === pieceIndex &&
                gap.runIndex === runIndex - 1
              );
            })
          : null;
      if (current && !(inner && choiceFor(inner) === CHOICE.REMOVE)) {
        current.lastVacation = run.lastVacation;
        return;
      }
      current = {
        leaveId: piece.leaveId,
        leaveEnd: piece.leaveEnd,
        firstVacation: run.firstVacation,
        lastVacation: run.lastVacation,
        coverageStart: runIndex === 0 ? piece.coverageStart : run.coverageStart,
        pieceIndex,
        removedGapBefore: inner || null,
      };
      segments.push(current);
    });
  });
  const originalSegment = segments.findIndex((segment) => {
    return segment.pieceIndex === (plan.originalIndex || 0);
  });
  const gapAfterSegment = (index) => {
    const next = segments[index + 1];
    if (next && next.removedGapBefore) {
      return next.removedGapBefore;
    }
    if (!next || next.pieceIndex !== segments[index].pieceIndex) {
      return outerGapAfter(segments[index].pieceIndex);
    }
    return null;
  };

  const starts = segments.map((segment, index) => {
    if (index === 0) {
      const leading = outerGapAfter(-1);
      if (leading && choiceFor(leading) === CHOICE.REMOVE) {
        return addDays(leading.dates[leading.dates.length - 1], 1);
      }
      return replacementStart;
    }
    return maxDate(
      segment.coverageStart,
      addDays(segments[index - 1].lastVacation, 1),
    );
  });

  return segments.map((segment, index) => {
    const isLast = index === segments.length - 1;
    const gap = gapAfterSegment(index);
    let end = minDate(
      segment.leaveEnd,
      isLast ? null : addDays(starts[index + 1], -1),
      replacementEnd,
    );
    if (gap && choiceFor(gap) === CHOICE.ATTACH) {
      end = isLast
        ? replacementEnd
        : maxDate(end, gap.dates[gap.dates.length - 1]);
    }
    if (gap && choiceFor(gap) === CHOICE.REMOVE) {
      end = minDate(end, addDays(gap.dates[0], -1));
    }
    end = maxDate(end, segment.lastVacation);
    return {
      leave_id: segment.leaveId,
      start_date: starts[index],
      end_date: end,
      original: index === originalSegment,
    };
  });
};

const formatSpan = (dates) => {
  if (dates.length === 1) {
    return `le ${formatDay(dates[0])}`;
  }
  return `du ${formatDay(dates[0])} au ${formatDay(dates[dates.length - 1])}`;
};

// Phrase de tête quand le congé lié ne couvre aucune vacation de R, d'après
// toutes ses absences sur le site (pas seulement celles de la période de R).
const leave1HeadWithoutCoverage = ({
  leave1,
  absentName,
  site,
  vacations,
  leave1Absences,
}) => {
  const first = vacations[0];
  const last = vacations[vacations.length - 1];
  if (!leave1Absences.length) {
    return `Le congé ${leave1} de ${absentName} n'a aucune absence sur ${site}.`;
  }
  const endsBefore = leave1Absences.every((absence) => {
    return absence.end_date && absence.end_date < first;
  });
  if (endsBefore) {
    return `Le congé ${leave1} de ${absentName} n'a plus d'absence sur ${site} à partir du ${formatDay(
      first,
    )}.`;
  }
  const startsAfter = leave1Absences.every((absence) => {
    return absence.start_date > last;
  });
  if (startsAfter) {
    const coverageStart = minDate(
      ...leave1Absences.map((absence) => {
        return absence.start_date;
      }),
    );
    return `Le congé ${leave1} de ${absentName} n'a pas d'absence sur ${site} avant le ${formatDay(
      coverageStart,
    )}.`;
  }
  return `Le congé ${leave1} de ${absentName} n'a pas d'absence sur ${site} ${formatSpan(
    [first, last],
  )}.`;
};

/**
 * @param {Object} input
 * @param {Object} input.replacement { start_date, end_date, weekdays[], leave_id,
 *   contract_id, account_name }
 * @param {string} input.absentName nom de l'absent (congé lié)
 * @param {Array} input.leaves congés de l'absent qui chevauchent R
 *   { id, start_date, end_date, contract_id }
 * @param {Array} input.absences absences de l'absent sur le compte de R
 *   { leave_id, start_date, end_date }
 * @param {Array} input.postes autres affectations de l'absent sur ce compte
 *   (hors Absence et Remplacement) { start_date, end_date }
 * @param {Array} [input.leave1Absences] toutes les absences du congé lié sur
 *   le compte, quelles que soient leurs dates (phrase de tête) ; à défaut,
 *   celles de `absences`
 */
export const planSplit = ({
  replacement,
  absentName,
  leaves: rawLeaves,
  absences: rawAbsences,
  postes: rawPostes,
  leave1Absences: rawLeave1Absences,
}) => {
  const leave1 = Number(replacement.leave_id);
  const site = replacement.account_name;
  const leaves = normalizeIntervals(rawLeaves);
  const absences = normalizeIntervals(rawAbsences);
  const postes = normalizeIntervals(rawPostes);
  const vacations = listVacations({
    start_date: normalizeDate(replacement.start_date),
    end_date: normalizeDate(replacement.end_date),
    weekdays: replacement.weekdays,
  });
  const coverages = buildCoverages(absences);
  const otherCoverages = Object.values(coverages)
    .filter((coverage) => {
      return coverage.leaveId !== leave1;
    })
    .sort((left, right) => {
      if (left.start !== right.start) {
        return left.start < right.start ? -1 : 1;
      }
      return left.leaveId - right.leaveId;
    });

  const base = {
    status: STATUS.COVERED,
    message: null,
    pieces: [],
    gaps: [],
    coverages,
    vacations,
  };

  if (!vacations.length) {
    return base;
  }

  // Chaque vacation va au congé lié s'il la couvre, sinon au premier congé
  // suivant qui la couvre, sinon à personne (orphelin).
  const assigned = vacations.map((date) => {
    if (coverages[leave1] && covers(coverages[leave1], date)) {
      return { date, leaveId: leave1 };
    }
    const other = otherCoverages.find((coverage) => {
      return covers(coverage, date);
    });
    return { date, leaveId: other ? other.leaveId : null };
  });

  if (
    assigned.every((vacation) => {
      return vacation.leaveId === leave1;
    })
  ) {
    return base;
  }

  const orphanDates = assigned
    .filter((vacation) => {
      return vacation.leaveId === null;
    })
    .map((vacation) => {
      return vacation.date;
    });

  // L1 : dernière vacation couverte par le congé lié ; X : première vacation
  // qui suit (« n'a plus d'absence … à partir du X »).
  const leave1Dates = assigned
    .filter((vacation) => {
      return vacation.leaveId === leave1;
    })
    .map((vacation) => {
      return vacation.date;
    });
  const lastLeave1 = leave1Dates.length
    ? leave1Dates[leave1Dates.length - 1]
    : null;
  const firstAfterLeave1 = lastLeave1
    ? vacations.find((date) => {
        return date > lastLeave1;
      })
    : vacations[0];
  const leave1EndsEarly = !!lastLeave1 && !!firstAfterLeave1;
  // Phrase de tête : fin de la première suite couverte par le congé lié
  // (jamais sa dernière vacation, qui peut suivre un trou ou un autre congé).
  const headAfter = (lastOfFirstPiece) => {
    const firstAfter = vacations.find((date) => {
      return date > lastOfFirstPiece;
    });
    return `Le congé ${leave1} de ${absentName} n'a plus d'absence sur ${site} à partir du ${formatDay(
      firstAfter || vacations[0],
    )}.`;
  };
  let head = headAfter(lastLeave1 || vacations[0]);

  // D5 : un congé de l'absent existe mais n'a pas encore d'absence sur ce
  // site → pas de découpage.
  const pendingLeaves = findPendingLeaves({
    dates: orphanDates,
    leaves,
    coverages,
    postes,
    contractId: replacement.contract_id,
  });
  if (pendingLeaves.length) {
    return {
      ...base,
      status: STATUS.PENDING,
      message: [
        ...(leave1EndsEarly ? [head] : []),
        ...pendingLeaves.map((leaveId) => {
          return `Le congé ${leaveId} n'a pas encore d'absence sur ce site.`;
        }),
      ].join(' '),
    };
  }

  // Morceaux (suites de vacations d'un même congé) et trous.
  const pieces = [];
  const gaps = [];
  let currentGap = null;
  assigned.forEach((vacation, index) => {
    if (vacation.leaveId === null) {
      if (!currentGap) {
        currentGap = {
          key: `gap-${gaps.length}`,
          afterPiece: pieces.length - 1,
          dates: [],
        };
        gaps.push(currentGap);
      }
      currentGap.dates.push(vacation.date);
      return;
    }
    const closingGap = currentGap;
    currentGap = null;
    const last = pieces[pieces.length - 1];
    const lastRun = last && last.runs[last.runs.length - 1];
    const previousIsSamePiece =
      last &&
      last.leaveId === vacation.leaveId &&
      assigned[index - 1].leaveId === vacation.leaveId;
    if (previousIsSamePiece) {
      last.lastVacation = vacation.date;
      lastRun.lastVacation = vacation.date;
      return;
    }
    // Trou entre deux suites du même congé : trou interne, même morceau.
    if (
      last &&
      last.leaveId === vacation.leaveId &&
      closingGap &&
      closingGap.afterPiece === pieces.length - 1
    ) {
      closingGap.position = 'inside';
      closingGap.runIndex = last.runs.length - 1;
      last.runs.push({
        firstVacation: vacation.date,
        lastVacation: vacation.date,
      });
      last.lastVacation = vacation.date;
      return;
    }
    pieces.push({
      leaveId: vacation.leaveId,
      firstVacation: vacation.date,
      lastVacation: vacation.date,
      runs: [{ firstVacation: vacation.date, lastVacation: vacation.date }],
    });
  });
  gaps.forEach((gap) => {
    /* eslint-disable no-param-reassign */
    if (gap.position === 'inside') {
      return;
    }
    if (gap.afterPiece < 0) {
      gap.position = 'before';
    } else if (gap.afterPiece === pieces.length - 1) {
      gap.position = 'after';
    } else {
      gap.position = 'between';
    }
    /* eslint-enable no-param-reassign */
  });

  if (!pieces.length) {
    return {
      ...base,
      status: STATUS.UNCOVERED,
      message: `Le congé ${leave1} de ${absentName} n'a aucune absence sur ${site} pendant ce remplacement. Aucun congé suivant ne couvre cette période.`,
      gaps,
    };
  }

  const leavesById = {};
  leaves.forEach((leave) => {
    leavesById[Number(leave.id)] = leave;
  });
  const decorated = pieces.map((piece) => {
    const coverage = coverages[piece.leaveId];
    const leave = leavesById[piece.leaveId];
    return {
      ...piece,
      runs: piece.runs.map((run) => {
        return {
          ...run,
          coverageStart: intervalStartFor(coverage, run.firstVacation),
        };
      }),
      coverageStart: intervalStartFor(coverage, piece.firstVacation),
      // Fin du congé (fiche congé) ; à défaut, fin de sa couverture.
      leaveEnd: leave
        ? leave.end_date
        : (!coverage.open && coverage.end) || null,
    };
  });

  gaps.forEach((gap) => {
    if (gap.position !== 'inside') {
      return;
    }
    const next = decorated[gap.afterPiece].runs[gap.runIndex + 1];
    // eslint-disable-next-line no-param-reassign
    gap.resumeStart = maxDate(
      next.coverageStart,
      addDays(gap.dates[gap.dates.length - 1], 1),
    );
  });
  const holeSentences = gaps
    .filter((gap) => {
      return gap.position === 'inside';
    })
    .map((gap) => {
      return `Le congé ${
        decorated[gap.afterPiece].leaveId
      } de ${absentName} n'a pas d'absence sur ${site} ${formatSpan(
        gap.dates,
      )}.`;
    });
  const withHoles = (message) => {
    return [message, ...holeSentences].filter(Boolean).join(' ');
  };

  // Le morceau du congé lié garde l'id de R ; sans morceau du congé lié,
  // R passe sur le premier congé qui le couvre (D3 bis).
  const leave1Index = decorated.findIndex((piece) => {
    return sameLeave(piece.leaveId, leave1);
  });
  const originalIndex = Math.max(0, leave1Index);
  const plan = {
    ...base,
    pieces: decorated,
    gaps,
    originalIndex,
  };
  const others = decorated.filter((piece, index) => {
    return index !== originalIndex;
  });
  if (leave1Index >= 0) {
    head = headAfter(decorated[leave1Index].lastVacation);
  } else {
    head = leave1HeadWithoutCoverage({
      leave1,
      absentName,
      site,
      vacations,
      leave1Absences: normalizeIntervals(
        rawLeave1Absences ||
          absences.filter((absence) => {
            return sameLeave(absence.leave_id, leave1);
          }),
      ),
    });
  }

  if (leave1Index >= 0 && !others.length) {
    const hasPosition = (position) => {
      return gaps.some((gap) => {
        return gap.position === position;
      });
    };
    if (hasPosition('after')) {
      return {
        ...plan,
        status: STATUS.NO_NEXT,
        message: withHoles(
          `${head} Aucun congé suivant ne couvre cette période.`,
        ),
      };
    }
    const beforeSentence = hasPosition('before')
      ? `Le congé ${leave1} de ${absentName} n'a pas d'absence sur ${site} avant le ${formatDay(
          decorated[0].firstVacation,
        )}.`
      : null;
    return {
      ...plan,
      status: holeSentences.length ? STATUS.HOLE : STATUS.ORPHANS,
      message: withHoles(beforeSentence),
    };
  }

  // Bornes annoncées : sans tenir compte des choix sur les trous.
  const defaultPieces = resolvePieces(
    plan,
    replacement,
    {},
    { ignoreGaps: true },
  );
  const original = defaultPieces[originalIndex];
  const stopAt = formatDay(original.end_date);
  const startAt = formatDay(original.start_date);
  const covered = formatLeaves(others, coverages);

  if (leave1Index < 0) {
    // D3 bis : R commence après la couverture de L1.
    let action = `rattachez ce remplacement au congé ${decorated[0].leaveId}`;
    if (others.length === 1) {
      action += `, arrêtez-le au ${stopAt}, puis créez-en un second sur le congé ${others[0].leaveId}`;
    } else if (others.length > 1) {
      action += `, arrêtez-le au ${stopAt}, puis créez-en un par congé suivant`;
    }
    return {
      ...plan,
      status: STATUS.RELINK,
      message: withHoles(
        `${head} ${formatLeaves(
          decorated,
          coverages,
        )} cette période : ${action}.`,
      ),
    };
  }

  const before = originalIndex > 0;
  const after = originalIndex < decorated.length - 1;
  const leave1Piece = decorated[originalIndex];
  let message;
  if (before && after) {
    message = `Le congé ${leave1} de ${absentName} n'a d'absence sur ${site} que du ${formatDay(
      leave1Piece.firstVacation,
    )} au ${formatDay(
      leave1Piece.lastVacation,
    )}. ${covered} le reste : limitez ce remplacement du ${startAt} au ${stopAt}, puis créez-en un par congé.`;
  } else if (before) {
    const action =
      others.length === 1
        ? `faites commencer ce remplacement au ${startAt}, puis créez-en un autre sur le congé ${others[0].leaveId}.`
        : `faites commencer ce remplacement au ${startAt}, puis créez-en un par congé.`;
    message = `Le congé ${leave1} de ${absentName} n'a pas d'absence sur ${site} avant le ${formatDay(
      leave1Piece.firstVacation,
    )}. ${covered} le début de ce remplacement : ${action}`;
  } else {
    const action =
      others.length === 1
        ? `arrêtez ce remplacement au ${stopAt}, puis créez-en un second sur le congé ${others[0].leaveId}.`
        : `arrêtez ce remplacement au ${stopAt}, puis créez-en un par congé.`;
    message = `${head} ${covered} cette période : ${action}`;
  }
  return { ...plan, status: STATUS.SPLIT, message: withHoles(message) };
};

// Vrai si les morceaux résolus ne changent rien à R (ex. trou final
// rattaché sans congé suivant) : rien à écrire.
export const isNoop = (resolved, replacement) => {
  return (
    !!resolved &&
    resolved.length === 1 &&
    resolved[0].original &&
    sameLeave(resolved[0].leave_id, replacement.leave_id) &&
    resolved[0].start_date === normalizeDate(replacement.start_date) &&
    resolved[0].end_date === normalizeDate(replacement.end_date)
  );
};

export const describeGap = (gap, absentName) => {
  const days = formatDates(gap.dates);
  const verb = gap.dates.length > 1 ? 'ne relèvent' : 'ne relève';
  return `${days.charAt(0).toUpperCase()}${days.slice(
    1,
  )} ${verb} d'aucun congé de ${absentName} sur ce site.`;
};

export const gapChoiceLabels = (gap, plan) => {
  if (gap.position === 'inside') {
    return {
      [CHOICE.ATTACH]: 'Les garder dans ce remplacement',
      [CHOICE.REMOVE]: `Les retirer (le remplacement reprend le ${formatDay(
        gap.resumeStart,
      )})`,
    };
  }
  if (gap.position === 'before') {
    return {
      [CHOICE.ATTACH]:
        (plan.originalIndex || 0) === 0
          ? 'Les garder dans ce remplacement'
          : `Les garder dans le remplacement du congé ${plan.pieces[0].leaveId}`,
      [CHOICE.REMOVE]: `Décaler le début au ${formatDay(
        addDays(gap.dates[gap.dates.length - 1], 1),
      )}`,
    };
  }
  const previous = plan.pieces[gap.afterPiece];
  return {
    [CHOICE.ATTACH]: `Les rattacher au morceau précédent (congé ${previous.leaveId})`,
    [CHOICE.REMOVE]: 'Les retirer',
  };
};
